import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { Card, Overlay, Popover } from 'react-bootstrap';
import CountUp from 'react-countup';
import { Link } from 'react-router-dom';
import Background from '../../common/Background';
import SubtleBadge from '../../common/SubtleBadge';
import formatCurrency from '../../../utils/formatCurrency';
import { useOrderContext } from '../../../providers/OrderProvider';
import { socket } from '../../../socket';
import OrderDetailModal from '../../../screens/order/modal/OrderDetailModal';
import bg1 from '../../../assets/img/icons/spot-illustrations/corner-1.png';
import bg2 from '../../../assets/img/icons/spot-illustrations/corner-2.png';
import bg3 from '../../../assets/img/icons/spot-illustrations/corner-3.png';
import bg4 from '../../../assets/img/icons/spot-illustrations/corner-4.png';

const convertToWan = (num) => {
	const numValue = typeof num === 'string' ? parseFloat(num) : num;
	if (typeof numValue !== 'number' || isNaN(numValue)) return 0;
	return numValue / 10000;
};

const TODAY_ORDERS_POPPER_CONFIG = {
	strategy: 'fixed',
	modifiers: [
		{ name: 'offset', options: { offset: [0, 12] } },
		{ name: 'preventOverflow', options: { padding: 12 } },
		{
			name: 'flip',
			options: {
				fallbackPlacements: ['right-start', 'bottom-start', 'left-start'],
			},
		},
	],
};

const TodayMarketingOrdersTrigger = ({ linkText, orders = [] }) => {
	const [show, setShow] = useState(false);
	const [isOpenOrderModal, setIsOpenOrderModal] = useState(false);
	const targetRef = useRef(null);
	const { setCurrentOrderOIdState } = useOrderContext();

	const handleOpenOrder = (orderOId) => {
		if (!orderOId) return;
		setShow(false);
		setIsOpenOrderModal(true);
		setCurrentOrderOIdState(orderOId);
		socket.emit('join order', orderOId);
	};

	return (
		<>
			<button
				ref={targetRef}
				type='button'
				className='fw-semibold fs-10 text-nowrap text-decoration-none dashboard-stat-card__today-orders-trigger'
				aria-label='查看今日订单'
				aria-expanded={show}
				onClick={(event) => {
					event.preventDefault();
					event.stopPropagation();
					setShow((open) => !open);
				}}
			>
				{linkText}
				<FontAwesomeIcon icon='angle-right' className='ms-1' transform='down-1' />
			</button>
			<Overlay
				show={show}
				target={targetRef}
				placement='right-start'
				rootClose
				flip
				transition={false}
				onHide={() => setShow(false)}
				popperConfig={TODAY_ORDERS_POPPER_CONFIG}
				container={typeof document !== 'undefined' ? document.body : undefined}
			>
				{(overlayProps) => (
					<Popover
						{...overlayProps}
						id='dashboard-today-marketing-orders'
						className={classNames(overlayProps.className, 'dashboard-stat-card__today-orders-popover')}
					>
						<Popover.Header as='div' className='dashboard-stat-card__today-orders-popover-title'>
							今日订单（{orders.length}）
						</Popover.Header>
						<Popover.Body className='dashboard-stat-card__today-orders-popover-body'>
							{orders.length === 0 ? (
								<div className='dashboard-stat-card__today-orders-popover-empty'>今日暂无已批准订单</div>
							) : (
								<ul className='dashboard-stat-card__today-orders-popover-list mb-0'>
									{orders.map((order) => (
										<li key={order._id ?? order.orderId} className='dashboard-stat-card__today-orders-popover-item'>
											<button
												type='button'
												className='dashboard-stat-card__today-orders-popover-order-link'
												onClick={(event) => {
													event.stopPropagation();
													handleOpenOrder(order._id);
												}}
											>
												{order.orderId}
											</button>
											<span className='dashboard-stat-card__today-orders-popover-customer' title={order.customerName || undefined}>
												{order.customerName || '—'}
											</span>
											<span className='dashboard-stat-card__today-orders-popover-amount'>{formatCurrency(order.totalPrice)}</span>
										</li>
									))}
								</ul>
							)}
						</Popover.Body>
					</Popover>
				)}
			</Overlay>
			<OrderDetailModal setIsOpenModal={setIsOpenOrderModal} isOpenModal={isOpenOrderModal} />
		</>
	);
};

TodayMarketingOrdersTrigger.propTypes = {
	linkText: PropTypes.string,
	orders: PropTypes.arrayOf(
		PropTypes.shape({
			_id: PropTypes.string,
			orderId: PropTypes.string.isRequired,
			customerName: PropTypes.string,
			totalPrice: PropTypes.number,
		}),
	),
};

const StatisticsCard = ({ stat, loadingStatsData, fetchingStatsData, className, ...rest }) => {
	const {
		title,
		value,
		decimal,
		suffix,
		prefix,
		valueClassName,
		badgeBg,
		badgeText,
		link,
		linkText,
		className: statClassName,
		secondaryValue,
		secondaryLabel,
		secondaryValueClassName,
		todayOrders,
		orderCount,
	} = stat;
	const useWanUnit = title === '总营销' || title === '总欠款';
	const hasSecondaryValue = secondaryValue !== undefined && secondaryLabel;
	const isUpdating = fetchingStatsData && !loadingStatsData;
	const hasRevealedRef = useRef(false);
	const [countPlay, setCountPlay] = useState({ animate: false, key: 0 });

	const numValue = typeof value === 'string' ? parseFloat(value) : value;
	const displayValue = useWanUnit ? convertToWan(numValue) : typeof numValue === 'number' && !isNaN(numValue) ? numValue : 0;
	const displaySuffix = useWanUnit ? '万' : (suffix ?? '');
	const displayDecimal = useWanUnit ? true : decimal;

	const secondaryNumValue = typeof secondaryValue === 'string' ? parseFloat(secondaryValue) : secondaryValue;
	const displaySecondaryValue = typeof secondaryNumValue === 'number' && !isNaN(secondaryNumValue) ? secondaryNumValue : 0;

	// 首次出数不滚动；之后数据变化再短动画，避免 KPI 从 0 滚很久造成主区域乱跳
	useEffect(() => {
		if (loadingStatsData) return;
		if (!hasRevealedRef.current) {
			hasRevealedRef.current = true;
			setCountPlay((prev) => ({ animate: false, key: prev.key + 1 }));
			return;
		}
		setCountPlay((prev) => ({ animate: true, key: prev.key + 1 }));
	}, [loadingStatsData, displayValue, displaySecondaryValue, orderCount]);

	const countDuration = countPlay.animate ? 0.8 : 0;
	const countStart = countPlay.animate ? 0 : displayValue;
	const secondaryCountStart = countPlay.animate ? 0 : displaySecondaryValue;

	return (
		<Card
			className={classNames('dashboard-stat-card', className, statClassName, {
				'dashboard-stat-card--has-secondary': hasSecondaryValue,
			})}
			data-stat-title={title}
			{...rest}
		>
			<>
				{title === '今日营销' && <Background image={bg1} className='bg-card' />}
				{title === '客户数量' && <Background image={bg2} className='bg-card' />}
				{title === '总营销' && <Background image={bg3} className='bg-card' />}
				{title === '总欠款' && <Background image={bg4} className='bg-card' />}
				<Card.Body className='dashboard-stat-card__body position-relative'>
					<h6 className='dashboard-stat-card__title'>
						{title}
						{badgeText && (
							<SubtleBadge bg={badgeBg} pill className='ms-2'>
								{badgeText}
							</SubtleBadge>
						)}
					</h6>

					{loadingStatsData ? (
						<div
							className={classNames('dashboard-stat-card__loading', {
								'dashboard-stat-card__loading--with-secondary': hasSecondaryValue,
							})}
							role='status'
							aria-live='polite'
							aria-label='加载统计数据'
						>
							<div className='dashboard-stat-card__skeleton dashboard-stat-card__skeleton--value' aria-hidden='true' />
							<div className='dashboard-stat-card__skeleton dashboard-stat-card__skeleton--foot' aria-hidden='true' />
							{hasSecondaryValue ? (
								<div className='dashboard-stat-card__skeleton dashboard-stat-card__skeleton--foot' aria-hidden='true' />
							) : null}
							<span className='visually-hidden'>加载中</span>
						</div>
					) : (
						/* 数值与脚注成组：手机端 body 为 space-between 时避免「查看全部」被拉开漂在中间 */
						<div className='dashboard-stat-card__main'>
							<div
								className={classNames(
									valueClassName,
									'dashboard-stat-card__value display-4 fs-5 mb-2 fw-normal font-sans-serif',
									isUpdating && 'opacity-75',
								)}
							>
								<CountUp
									key={`v-${countPlay.key}`}
									start={countStart}
									end={displayValue}
									duration={countDuration}
									suffix={displaySuffix}
									prefix={prefix ?? ''}
									separator=','
									decimals={displayDecimal ? 2 : 0}
									decimal='.'
								/>
							</div>
							{hasSecondaryValue ? (
								<div className={classNames('mb-0', 'mt-2', isUpdating && 'opacity-75')} style={{ marginTop: '-0.5rem' }}>
									<span className='fw-semibold fs-10 text-nowrap'>
										{secondaryLabel}:{' '}
										<span className={secondaryValueClassName}>
											<CountUp
												key={`s-${countPlay.key}`}
												start={secondaryCountStart}
												end={displaySecondaryValue}
												duration={countDuration}
												separator=','
												decimals={0}
											/>
										</span>
									</span>
								</div>
							) : title === '今日营销' ? (
								<TodayMarketingOrdersTrigger linkText={linkText} orders={todayOrders ?? []} />
							) : (
								<div className='dashboard-stat-card__foot'>
									{title === '总营销' && orderCount !== undefined && orderCount !== null ? (
										<span className='fw-semibold fs-10 text-nowrap dashboard-stat-card__order-count'>
											订单数量{' '}
											<span className={isUpdating ? 'opacity-75' : undefined}>
												<CountUp
													key={`c-${countPlay.key}`}
													start={countPlay.animate ? 0 : Number(orderCount) || 0}
													end={Number(orderCount) || 0}
													duration={countDuration}
													separator=','
													decimals={0}
												/>
											</span>
										</span>
									) : null}
									<Link to={link} className='fw-semibold fs-10 text-nowrap'>
										{linkText}
										<FontAwesomeIcon icon='angle-right' className='ms-1' transform='down-1' />
									</Link>
								</div>
							)}
						</div>
					)}
				</Card.Body>
			</>
		</Card>
	);
};

StatisticsCard.propTypes = {
	stat: PropTypes.shape({
		title: PropTypes.string.isRequired,
		value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
		decimal: PropTypes.bool,
		suffix: PropTypes.string,
		prefix: PropTypes.string,
		valueClassName: PropTypes.string,
		linkText: PropTypes.string,
		link: PropTypes.string,
		badgeText: PropTypes.string,
		badgeBg: PropTypes.string,
		image: PropTypes.string,
		className: PropTypes.string,
		secondaryValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
		secondaryLabel: PropTypes.string,
		secondaryValueClassName: PropTypes.string,
		orderCount: PropTypes.number,
		todayOrders: PropTypes.arrayOf(
			PropTypes.shape({
				_id: PropTypes.string,
				orderId: PropTypes.string.isRequired,
				customerName: PropTypes.string,
				totalPrice: PropTypes.number,
			}),
		),
	}),
	loadingStatsData: PropTypes.bool,
	fetchingStatsData: PropTypes.bool,
};
export default StatisticsCard;
